/**
 * useRoleOptions
 *
 * Fetches the org's RBAC roles (GET /api/rbac/roles) and maps them to
 * select options for the user create/edit forms — so the role list is driven
 * by the backend instead of a hardcoded enum.
 *
 * The option `value` is the role `code` (what the org-user create/update
 * endpoint stores in `listOfRoles`); the `label` is the human-readable name.
 */

import { useEffect, useState } from 'react';

import { rbacService } from '@/api/services';

export interface RoleOption {
  value: string;
  label: string;
}

export function useRoleOptions() {
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const response = await rbacService.getRoles();
        if (active && response.statusCode === 200 && response.result) {
          setRoleOptions(
            response.result.map((r) => ({ value: r.code, label: r.name }))
          );
        }
      } catch (error) {
        console.error('Failed to load roles:', error);
      } finally {
        if (active) setRolesLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return { roleOptions, rolesLoading };
}
